/**
 * `useUpdateApprovalChain` mutation hook.
 *
 * Wraps the raw `updateApprovalChain` service fn (per AD-23). A full-replace update;
 * per the approval design, changes take effect immediately on in-progress
 * assets. We invalidate every approval chain query under the project so both the list
 * and the edited detail refetch.
 *
 * The step rewrite moves the approval progress of every already-assigned asset,
 * so the folder + asset-approval-chain caches are busted on any edit — not only
 * when `autoApplyToExistingFolders` pulls in new assets. See
 * `invalidate-project-chain-assignments.ts`.
 *
 * Side effects: NONE. Toast + navigation + the "active on X assets" confirm
 * belong to the caller (the editor).
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  APPROVAL_CHAIN_QUERY_KEYS,
  type UpdateApprovalChainInput,
} from '@/entities/approval-chain';

import { invalidateProjectChainAssignments } from './invalidate-project-chain-assignments';
import { updateApprovalChain } from './approval-chains.api';

export { updateApprovalChain };

interface UseUpdateApprovalChainOptions {
  projectId: string;
}

interface UpdateApprovalChainVariables {
  approvalChainId: string;
  body: UpdateApprovalChainInput;
}

export function useUpdateApprovalChain(options: UseUpdateApprovalChainOptions) {
  const queryClient = useQueryClient();
  const { projectId } = options;

  return useMutation({
    mutationFn: ({ approvalChainId, body }: UpdateApprovalChainVariables) =>
      updateApprovalChain(projectId, approvalChainId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: APPROVAL_CHAIN_QUERY_KEYS.all(projectId),
      });

      invalidateProjectChainAssignments(queryClient, projectId);
    },
  });
}
